import { countMappedRecipeCoverage, PIECE_STATUS } from './programCompleteness';
import { buildWeekWorkshopModel } from './weekWorkshop';

export const DELIVERY_WORKSPACE_STATUS = Object.freeze({
  COMPLETE: 'complete',
  CHECK: 'check',
  MISSING: 'missing',
  REVIEW: 'review',
});

const STATUS_LABELS = Object.freeze({
  [DELIVERY_WORKSPACE_STATUS.COMPLETE]: 'Complet',
  [DELIVERY_WORKSPACE_STATUS.CHECK]: 'À vérifier',
  [DELIVERY_WORKSPACE_STATUS.MISSING]: 'Manquant',
  [DELIVERY_WORKSPACE_STATUS.REVIEW]: 'À revoir',
});

function pieceStatus(piece) {
  if (piece?.status === PIECE_STATUS.READY) return DELIVERY_WORKSPACE_STATUS.COMPLETE;
  if (piece?.status === PIECE_STATUS.PARTIAL) return DELIVERY_WORKSPACE_STATUS.CHECK;
  if (piece?.status === PIECE_STATUS.TODO) return DELIVERY_WORKSPACE_STATUS.MISSING;
  return DELIVERY_WORKSPACE_STATUS.REVIEW;
}

function section(id, label, description, status, actionLabel, statusLabel = null) {
  return Object.freeze({
    id,
    label,
    description,
    status,
    statusLabel: statusLabel || STATUS_LABELS[status],
    actionLabel,
  });
}

// Statuts de l'éditeur « Semaine, menus & recettes » : un vrai état de
// relecture (incomplet / à valider / validé), plus un simple ratio de recettes.
function weekMenusSectionState(weekMenus) {
  // Contenu déjà en ligne, jamais retouché dans l'éditeur : neutre.
  if (weekMenus.publishedBaseline) {
    return {
      status: DELIVERY_WORKSPACE_STATUS.COMPLETE,
      label: 'Déjà publié',
      description: 'Contenu déjà publié — à revalider si tu le modifies.',
    };
  }
  if (weekMenus.status === 'validated') {
    return { status: DELIVERY_WORKSPACE_STATUS.COMPLETE, label: 'Validé', description: 'Les repas des 4 semaines sont complets et validés.' };
  }
  if (weekMenus.status === 'incomplete') {
    const count = weekMenus.issues.length;
    return {
      status: DELIVERY_WORKSPACE_STATUS.CHECK,
      label: 'Incomplet',
      description: `${weekMenus.issues[0]}${count > 1 ? ` (+${count - 1} autre${count > 2 ? 's' : ''} point${count > 2 ? 's' : ''})` : ''}.`,
    };
  }
  const toValidate = weekMenus.slots.filter((slot) => slot.status !== 'validated').map((slot) => slot.label);
  return {
    status: DELIVERY_WORKSPACE_STATUS.REVIEW,
    label: 'À valider',
    description: `Complet, à valider : ${toValidate.join(', ')}.`,
  };
}

function weekMenusRotationState(rotation) {
  if (rotation.status === 'validated') {
    return { status: DELIVERY_WORKSPACE_STATUS.COMPLETE, label: 'Validée', description: 'Les choix qui font varier le mois.' };
  }
  if (rotation.status === 'to_refresh') {
    return { status: DELIVERY_WORKSPACE_STATUS.REVIEW, label: 'À actualiser', description: rotation.message };
  }
  return {
    status: DELIVERY_WORKSPACE_STATUS.CHECK,
    label: rotation.statusLabel,
    description: rotation.issues[0] || rotation.message || 'Les choix qui font varier le mois.',
  };
}

function countFridgeItems(fridge) {
  const essentials = Array.isArray(fridge?.essentials) ? fridge.essentials.length : 0;
  const nestedItems = ['favorite', 'limit'].reduce((total, key) => total
    + (Array.isArray(fridge?.[key])
      ? fridge[key].reduce((count, category) => count
        + (Array.isArray(category?.items) ? category.items.length : 0), 0)
      : 0), 0);
  return essentials + nestedItems;
}

export function buildDeliveryWorkspaceModel({
  plan,
  completeness,
  editorialIssue = null,
  weekMenus = null,
} = {}) {
  const sections = plan?.sections || {};
  const weekMeals = sections.week_meals || null;
  const pieces = new Map((completeness?.pieces || []).map((piece) => [piece.key, piece]));
  const weekModel = buildWeekWorkshopModel(weekMeals);
  const recipeCoverage = countMappedRecipeCoverage(weekMeals);
  const recipes = { ready: recipeCoverage.covered, total: recipeCoverage.total };
  const mealPrep = weekMeals?.prep;
  const rotation = weekMeals?.rotation;
  const fridgeItems = countFridgeItems(sections.fridge_data);
  const supplementGroups = Array.isArray(sections.protocols_data?.groups)
    ? sections.protocols_data.groups.length
    : 0;
  const mealsPerDay = Array.isArray(weekMeals?.days?.[0]?.meals)
    ? weekMeals.days[0].meals.length
    : 0;

  const weekStatus = weekModel.dayCount === 7 && mealsPerDay > 0
    ? DELIVERY_WORKSPACE_STATUS.COMPLETE
    : DELIVERY_WORKSPACE_STATUS.MISSING;
  const menuStatus = recipes.total === 0
    ? DELIVERY_WORKSPACE_STATUS.MISSING
    : (recipes.ready === recipes.total
      ? DELIVERY_WORKSPACE_STATUS.COMPLETE
      : DELIVERY_WORKSPACE_STATUS.CHECK);
  const weekAndMenusStatus = weekStatus === DELIVERY_WORKSPACE_STATUS.COMPLETE
    ? menuStatus
    : DELIVERY_WORKSPACE_STATUS.MISSING;
  const rotationStatus = rotation?.version === 1 && Array.isArray(rotation?.weeks)
    && rotation.weeks.length === 4
    ? DELIVERY_WORKSPACE_STATUS.COMPLETE
    : DELIVERY_WORKSPACE_STATUS.CHECK;
  const mealPrepSessions = Array.isArray(mealPrep?.sessions) ? mealPrep.sessions : [];
  const mealPrepV1Ready = mealPrep?.version === 1
    && mealPrepSessions.length > 0
    && mealPrepSessions.every((sessionItem) => Array.isArray(sessionItem?.tasks) && sessionItem.tasks.length > 0);
  const mealPrepV2Ready = mealPrep?.version === 2
    && Number.isInteger(mealPrep.session_count) && mealPrep.session_count > 0
    && mealPrepSessions.length === mealPrep.session_count
    && mealPrepSessions.every((sessionItem) => Array.isArray(sessionItem?.components) && sessionItem.components.length > 0);
  const mealPrepStatus = mealPrepV1Ready || mealPrepV2Ready
    ? DELIVERY_WORKSPACE_STATUS.COMPLETE
    : DELIVERY_WORKSPACE_STATUS.CHECK;
  const supplementsStatus = supplementGroups > 0
    ? pieceStatus(pieces.get('supplements'))
    : DELIVERY_WORKSPACE_STATUS.MISSING;

  const weekMenusState = weekMenus && weekStatus === DELIVERY_WORKSPACE_STATUS.COMPLETE
    && weekMenus.slots?.length > 0
    ? weekMenusSectionState(weekMenus)
    : null;
  const rotationState = weekMenusState && weekMenus.rotation
    ? weekMenusRotationState(weekMenus.rotation)
    : null;

  const items = [
    section('introduction', 'Introduction', 'Le message d’accueil de la cliente.', pieceStatus(pieces.get('intro')), 'Ouvrir'),
    section('strategy', 'Stratégie', 'Les priorités et repères du programme.', pieceStatus(pieces.get('strategy')), 'Ouvrir'),
    weekMenusState
      ? section('week', 'Semaine, menus & recettes', weekMenusState.description, weekMenusState.status, 'Ouvrir', weekMenusState.label)
      : section('week', 'Semaine, menus & recettes', 'Les sept jours, leurs alternatives et les recettes validées.', weekAndMenusStatus, 'Vérifier'),
    rotationState
      ? section('rotation', 'Rotation 4 semaines', rotationState.description, rotationState.status, 'Vérifier', rotationState.label)
      : section('rotation', 'Rotation 4 semaines', 'Les choix qui font varier le mois.', rotationStatus, 'Vérifier'),
    section('meal-prep', mealPrep?.version === 2 ? 'Meal Prep v2' : 'Meal Prep', 'Les sessions et préparations groupées.', mealPrepStatus, 'Ouvrir'),
    section('fridge', 'Frigo & courses', 'Les essentiels et la liste de courses.', pieceStatus(pieces.get('fridge')), 'Vérifier'),
    section('supplements', 'Compléments', 'Les moments de prise visibles dans l’app.', supplementsStatus, 'Ouvrir'),
  ];

  const repeatedMeals = weekModel.days
    .flatMap((day) => day.meals)
    .filter((meal, index, all) => meal.occurrenceCount > 1
      && all.findIndex((candidate) => candidate.recipeKey === meal.recipeKey) === index)
    .map((meal) => ({
      title: meal.title,
      count: meal.occurrenceCount,
    }));

  const pending = items.filter((item) => item.status !== DELIVERY_WORKSPACE_STATUS.COMPLETE);
  if (editorialIssue) {
    pending.unshift(Object.freeze({
      id: 'editorial-state',
      label: 'Modification en attente',
      description: editorialIssue,
      status: DELIVERY_WORKSPACE_STATUS.REVIEW,
      statusLabel: STATUS_LABELS[DELIVERY_WORKSPACE_STATUS.REVIEW],
      actionLabel: 'Vérifier',
    }));
  }

  return Object.freeze({
    items: Object.freeze(items),
    pending: Object.freeze(pending),
    summary: Object.freeze({
      mealsPerDay,
      supplementGroups,
      fridgeItems,
      recipesReady: recipes.ready,
      recipesTotal: recipes.total,
      mealPrepSessions: mealPrep?.version === 2 && Number.isInteger(mealPrep?.session_count)
        ? mealPrep.session_count
        : mealPrepSessions.length,
      completeCount: items.filter((item) => item.status === DELIVERY_WORKSPACE_STATUS.COMPLETE).length,
      totalCount: items.length,
    }),
    repeatedMeals: Object.freeze(repeatedMeals),
  });
}

export function deliveryWorkspaceTarget(id) {
  if (id === 'repetitions' || id === 'menus') return 'week';
  if (id === 'editorial-state') return 'package';
  return id;
}

/**
 * Section de l'aperçu où corriger une pièce signalée incomplète.
 *
 * Revue ergo 2026-08-21 — l'étape Livraison listait les pièces avec leur
 * statut (« Compléments : à compléter ») dans des lignes inertes, sous un
 * unique bouton « Ouvrir l'aperçu complet ». Anissa lisait donc ce qui
 * manquait, puis devait retrouver l'endroit toute seule. Chaque ligne mène
 * maintenant à sa section.
 *
 * Les clés viennent de `programCompleteness`, les cibles sont les sections de
 * l'aperçu : deux vocabulaires distincts, d'où cette table explicite.
 *
 * `plan` et `phases` n'ont volontairement pas de cible : le plan n'est pas une
 * section de l'app, et les phases se règlent ailleurs. Une ligne sans cible
 * reste inerte plutôt que de mener au mauvais endroit.
 *
 * @param {string} key - clé de pièce (`intro`, `strategy`, `recipes`, …)
 * @returns {string|null} identifiant de section, ou null si non navigable
 */
const PIECE_TO_SECTION = Object.freeze({
  intro: 'introduction',
  strategy: 'strategy',
  recipes: 'week',
  fridge: 'fridge',
  supplements: 'supplements',
});

export function sectionForCompletenessPiece(key) {
  return PIECE_TO_SECTION[key] || null;
}
